import AbstractService from '~/composables/analytics/services/AbstractService'
import DataLayer from '~/composables/analytics/services/dataLayer'
import FacebookPixel from '~/composables/analytics/services/FacebookPixel'
import Klaviyo from '~/composables/analytics/services/Klaviyo'
import Criteo from '~/composables/analytics/services/Criteo'
import type ProductImpressionData from '~/types/analytics/marketplace/ProductImpressionData'
import type ProductClickData from '~/types/analytics/marketplace/ProductClickData'
import type ProductViewData from '~/types/analytics/marketplace/ProductViewData'
import type ProductAddedToCartData from '~/types/analytics/marketplace/ProductAddedToCartData'
import type CartViewData from '~/types/analytics/marketplace/CartViewData'
import type BeginCheckoutData from '~/types/analytics/marketplace/BeginCheckoutData'
import type AddPaymentInfoData from '~/types/analytics/marketplace/AddPaymentInfoData'
import type PayButtonClickedData from '~/types/analytics/marketplace/PayButtonClickedData'
import type PaymentSuccessData from '~/types/analytics/checkout/PaymentSuccessData'
import type PaymentDeclinedData from '~/types/analytics/checkout/PaymentDeclinedData'
import type ShowMoreProductsData from '~/types/analytics/marketplace/search/ShowMoreProductsData'
import type ManualSearchData from '~/types/analytics/marketplace/search/ManualSearchData'
import type RefinedSearchData from '~/types/analytics/marketplace/search/RefinedSearchData'
import type QuotePaymentPageViewData from '~/types/analytics/quotes/checkout/QuotePaymentPageViewData'
import type QuotePaymentPageAddedDeliveryInfoData from '~/types/analytics/quotes/checkout/QuotePaymentPageAddedDeliveryInfoData'
import type QuotePaymentPageAddedPaymentInfoData from '~/types/analytics/quotes/checkout/QuotePaymentPageAddedPaymentInfoData'
import type QuotePaymentPagePayButtonClickData from '~/types/analytics/quotes/checkout/QuotePaymentPagePayButtonClickData'

export const useAnalyticalDataTracking = () => {
  function getTrackingServices(): AbstractService[] {
    return [new DataLayer(), new FacebookPixel(), new Klaviyo(), new Criteo()]
  }

  const trackingServices: AbstractService[] = getTrackingServices()

  async function trackQuoteSubmit(quoteId: number) {
    for (const predicate of trackingServices) {
      await predicate.trackQuoteSubmit(quoteId)
    }
  }

  async function trackPageView(eventName: string | null): Promise<void> {
    for (const predicate of trackingServices) {
      await predicate.trackPageView({
        eventName,
      })
    }
  }

  async function trackQuotePaid(quoteId: number, valueInUSD: number) {
    for (const predicate of trackingServices) {
      await predicate.trackQuotePaid(quoteId, valueInUSD)
    }
  }

  async function trackPageLoad() {
    for (const predicate of trackingServices) {
      await predicate.trackPageLoad()
    }

    await trackPageView(null)
  }

  async function trackMarketplaceProductImpressions(impressions: ProductImpressionData[]) {
    for (const predicate of trackingServices) {
      await predicate.trackMarketplaceProductImpressions(impressions)
    }
  }

  async function trackMarketplaceProductClick(data: ProductClickData) {
    for (const predicate of trackingServices) {
      await predicate.trackMarketplaceProductClick(data)
    }
  }

  async function trackMarketplaceProductView(data: ProductViewData) {
    for (const predicate of trackingServices) {
      await predicate.trackMarketplaceProductView(data)
    }
  }

  async function trackMarketplaceProductAddedToCart(data: ProductAddedToCartData) {
    for (const predicate of trackingServices) {
      await predicate.trackMarketplaceProductAddedToCart(data)
    }
  }

  async function trackViewCart(data: CartViewData) {
    for (const predicate of trackingServices) {
      await predicate.trackViewCart(data)
    }
  }

  async function trackBeginCheckout(data: BeginCheckoutData) {
    for (const predicate of trackingServices) {
      await predicate.trackBeginCheckout(data)
    }
  }

  async function trackMarketplaceAddPaymentInfo(data: AddPaymentInfoData) {
    for (const predicate of trackingServices) {
      await predicate.trackMarketplaceAddPaymentInfo(data)
    }
  }

  async function trackMarketplacePayButtonClick(data: PayButtonClickedData) {
    for (const predicate of trackingServices) {
      await predicate.trackMarketplacePayButtonClick(data)
    }
  }

  async function trackPaymentSuccessPage(data: PaymentSuccessData) {
    for (const predicate of trackingServices) {
      await predicate.trackPaymentSuccessPage(data)
    }
  }

  async function trackPaymentDeclinedPage(data: PaymentDeclinedData) {
    for (const predicate of trackingServices) {
      await predicate.trackPaymentDeclinedPage(data)
    }
  }

  async function trackMarketplaceSearchBarShowMoreProducts(data: ShowMoreProductsData) {
    for (const predicate of trackingServices) {
      await predicate.trackMarketplaceSearchBarShowMoreProducts(data)
    }
  }

  async function trackMarketplaceSearchBarManualSearch(data: ManualSearchData) {
    for (const predicate of trackingServices) {
      await predicate.trackMarketplaceSearchBarManualSearch(data)
    }
  }

  async function trackMarketplaceSearchBarRefinedSearch(data: RefinedSearchData) {
    for (const predicate of trackingServices) {
      await predicate.trackMarketplaceSearchBarRefinedSearch(data)
    }
  }

  async function trackQuotePaymentPageView(data: QuotePaymentPageViewData) {
    for (const predicate of trackingServices) {
      await predicate.trackQuotePaymentPageView(data)
    }
  }

  async function trackQuotePaymentPageAddedDeliveryInfo(data: QuotePaymentPageAddedDeliveryInfoData) {
    for (const predicate of trackingServices) {
      await predicate.trackQuotePaymentPageAddedDeliveryInfo(data)
    }
  }

  async function trackQuotePaymentPageAddedPaymentInfo(data: QuotePaymentPageAddedPaymentInfoData) {
    for (const predicate of trackingServices) {
      await predicate.trackQuotePaymentPageAddedPaymentInfo(data)
    }
  }

  async function trackQuotePaymentPagePayButtonClick(data: QuotePaymentPagePayButtonClickData) {
    for (const predicate of trackingServices) {
      await predicate.trackQuotePaymentPagePayButtonClick(data)
    }
  }

  return {
    trackPageView,
    trackPageLoad,
    trackQuoteSubmit,
    trackQuotePaid,
    trackMarketplaceProductImpressions,
    trackMarketplaceProductClick,
    trackMarketplaceProductView,
    trackMarketplaceProductAddedToCart,
    trackViewCart,
    trackBeginCheckout,
    trackMarketplaceAddPaymentInfo,
    trackMarketplacePayButtonClick,
    trackPaymentSuccessPage,
    trackPaymentDeclinedPage,
    trackMarketplaceSearchBarShowMoreProducts,
    trackMarketplaceSearchBarManualSearch,
    trackMarketplaceSearchBarRefinedSearch,
    trackQuotePaymentPageView,
    trackQuotePaymentPageAddedDeliveryInfo,
    trackQuotePaymentPageAddedPaymentInfo,
    trackQuotePaymentPagePayButtonClick,
  }
}
